import { useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { ROUTES } from "@/router";
import { useAppStore } from "@/store/app/slice";
import { AuthState } from "@/types/common";
import {
  UserLoadingStatus,
  UserOnboardingStatus,
  UserRole,
} from "@/types/users";
import { getTenantFromUrl } from "@/utils/getTenantFromUrl";

/**
 * Centralized hook for handling authentication redirects.
 * Determines where to redirect users based on:
 * 1. Special user types (SuperAdmin, Onboarding)
 * 2. Number of tenants (0, 1, or multiple)
 * 3. Previously attempted page (stored in localStorage)
 * 4. Default route
 *
 * All routes are tenant-scoped: /:tenant/*
 * SuperAdmin uses "admin" as their tenant.
 * Users with multiple tenants are redirected to tenant selection page.
 */
export const useAuthenticationRedirect = () => {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  const authenticated = useAppStore((s) => s.authenticated);
  const user = useAppStore((s) => s.user);
  const userLoaded = useAppStore((s) => s.userLoaded);
  const userTenants = useAppStore((s) => s.userTenants);
  const userTenantsLoaded = useAppStore((s) => s.userTenantsLoaded);

  useEffect(() => {
    const pathname = window.location.pathname;

    if (pathname.includes(`/${ROUTES.LEGAL}`)) {
      return;
    }

    if (authenticated !== AuthState.Authenticated) {
      return;
    }

    if (
      userLoaded === UserLoadingStatus.NotStarted ||
      userLoaded === UserLoadingStatus.Loading
    ) {
      return;
    }

    if (!userTenantsLoaded) {
      return;
    }

    if (userLoaded === UserLoadingStatus.Pending) {
      if (!pathname.includes(ROUTES.SELECT_TENANT)) {
        navigateRef.current(`/${ROUTES.SELECT_TENANT}`);
      }
      return;
    }

    if (!user) {
      return;
    }

    // Only redirect when authenticated and user data is loaded
    // Priority 1: SuperAdmin goes to tenants page
    if (user.role === UserRole.SuperAdmin) {
      navigateRef.current(`/admin/${ROUTES.TENANTS}`);
      return;
    }

    // Priority 2: Resolve tenant or redirect to tenant selection
    const urlTenant = getTenantFromUrl();

    if (!userTenants || userTenants.length === 0) {
      if (!pathname.includes(ROUTES.SELECT_TENANT)) {
        navigateRef.current(`/${ROUTES.SELECT_TENANT}`);
      }
      return;
    }

    const tenant =
      userTenants.length === 1
        ? encodeURIComponent(userTenants[0])
        : urlTenant
          ? encodeURIComponent(urlTenant)
          : "";

    if (!tenant) {
      // Check if registration flow set a target URL (skip workspace selection)
      const savedUrl = useAppStore.getState().savedUrl;
      if (savedUrl && savedUrl !== "/" && !savedUrl.includes(ROUTES.AUTH)) {
        useAppStore.getState().setSavedUrl(null);
        navigateRef.current(savedUrl);
        return;
      }

      if (!pathname.includes(ROUTES.SELECT_TENANT)) {
        navigateRef.current(`/${ROUTES.SELECT_TENANT}`);
      }
      return;
    }

    // Priority 3: Onboarding users
    if (user.onboardingStatus === UserOnboardingStatus.AccountClaimed) {
      navigateRef.current(`/${tenant}/${ROUTES.ONBOARDING}`);
      return;
    }

    // Priority 4: Previous page (where user tried to go before auth)
    const previousPage = useAppStore.getState().savedUrl;
    if (
      previousPage &&
      previousPage !== "/" &&
      !previousPage.includes(ROUTES.AUTH)
    ) {
      useAppStore.getState().setSavedUrl(null);
      navigateRef.current(previousPage);
      return;
    }

    // Priority 5: Default route - projects page
    if (pathname === "/" || pathname.includes(ROUTES.AUTH)) {
      navigateRef.current(`/${tenant}/${ROUTES.PROJECTS}`);
    }
  }, [authenticated, user, userTenants]);
};
